import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig, getThemeTranslation } from '@theme/utilities';
import {
  extractNumericId,
  getVariantOptionValue,
  isVariantAvailable,
  normalizeVariantName,
} from '@theme/product-card-helpers';
import { isEarlyAccessGatingQuickShop } from '@theme/early-access-core';
import { resolveEarlyAccessGrant } from '@theme/early-access';

/**
 * @typedef {import('@theme/product-card-helpers').ProductCardData} ProductCardData
 * @typedef {import('@theme/product-card-helpers').ProductVariant} ProductVariant
 * @typedef {import('@theme/product-card-helpers').SelectedOption} SelectedOption
 *
 * @typedef {object} ProductCardQuickShopViewModel
 * @property {HTMLElement|null} [$imageWrapper]
 */

const QUICK_SHOP_CLASSIFICATION = 'Sportswear';
const QUICK_SHOP_SIZE_OPTION_NAME = 'Size';
const QUICK_SHOP_DEFAULT_COLUMNS = 3;

const QUICK_SHOP_SELECTORS = {
  ROOT: '[data-product-card-quick-shop]',
  TOGGLE: '[data-product-card-quick-shop-toggle]',
  PANEL: '[data-product-card-quick-shop-panel]',
  GRID: '[data-product-card-quick-shop-grid]',
};

let productCardQuickShopId = 0;

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeComparable(value) {
  return normalizeVariantName(String(value || ''));
}

/**
 * @param {string|null|undefined} left
 * @param {string|null|undefined} right
 * @returns {boolean}
 */
function optionValuesMatch(left, right) {
  return normalizeComparable(left) === normalizeComparable(right);
}

/**
 * @param {Element|null|undefined} element
 * @returns {HTMLElement|null}
 */
function asHTMLElement(element) {
  return element instanceof HTMLElement ? element : null;
}

/**
 * @param {Element|null|undefined} element
 * @returns {HTMLButtonElement|null}
 */
function asHTMLButtonElement(element) {
  return element instanceof HTMLButtonElement ? element : null;
}

/**
 * @returns {string[]}
 */
function getCartSectionIds() {
  const sectionIds = new Set();

  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionIds.add(item.dataset.sectionId);
    }
  });

  document
    .querySelectorAll('[data-cart-refresh-section-id]')
    .forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.cartRefreshSectionId) {
        sectionIds.add(item.dataset.cartRefreshSectionId);
      }
    });

  return Array.from(sectionIds);
}

export class ProductCardQuickShopController {
  /**
   * @param {{
   *   root: HTMLElement,
   *   viewModel: ProductCardQuickShopViewModel,
   *   data: ProductCardData
   * }} opts
   */
  constructor({ root, viewModel, data }) {
    this.root = root;
    this.viewModel = viewModel;
    this.productData = data;
    this.quickShopId = ++productCardQuickShopId;
    /** @type {AbortController|null} */
    this.abortController = null;
    /** @type {HTMLElement|null} */
    this.$quickShop = null;
    /** @type {HTMLButtonElement|null} */
    this.$toggle = null;
    /** @type {HTMLElement|null} */
    this.$panel = null;
    /** @type {HTMLElement|null} */
    this.$grid = null;
    this.isOpen = false;
    this.isAdding = false;

    this.handleToggleClickBound = this.handleToggleClick.bind(this);
    this.handleSizeClickBound = this.handleSizeClick.bind(this);
    this.handleKeydownBound = this.handleKeydown.bind(this);
    this.handleDocumentPointerDownBound =
      this.handleDocumentPointerDown.bind(this);
  }

  initialize() {
    this.cacheElements();
    if (!this.$quickShop || !this.$toggle || !this.$panel || !this.$grid) {
      return;
    }

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.$toggle.addEventListener('click', this.handleToggleClickBound, {
      signal,
    });
    this.$grid.addEventListener('click', this.handleSizeClickBound, { signal });
    this.root.addEventListener('keydown', this.handleKeydownBound, { signal });
    document.addEventListener(
      'pointerdown',
      this.handleDocumentPointerDownBound,
      { capture: true, signal }
    );

    this.update(this.productData);
  }

  cacheElements() {
    const imageWrapper = this.viewModel.$imageWrapper;

    this.$quickShop = asHTMLElement(
      imageWrapper?.querySelector(QUICK_SHOP_SELECTORS.ROOT)
    );
    this.$toggle = asHTMLButtonElement(
      this.$quickShop?.querySelector(QUICK_SHOP_SELECTORS.TOGGLE)
    );
    this.$panel = asHTMLElement(
      this.$quickShop?.querySelector(QUICK_SHOP_SELECTORS.PANEL)
    );
    this.$grid = asHTMLElement(
      this.$quickShop?.querySelector(QUICK_SHOP_SELECTORS.GRID)
    );

    if (this.$panel && this.$toggle) {
      this.$panel.id = `product-card-quick-shop-panel-${this.quickShopId}`;
      this.$toggle.setAttribute('aria-controls', this.$panel.id);
    }
  }

  /** @param {ProductCardData} data */
  update(data) {
    this.productData = data;

    if (!this.shouldEnable()) {
      this.deactivate();
      return;
    }

    this.activate();
  }

  shouldEnable() {
    const classification = String(
      this.productData?.flags?.productClassification || ''
    ).trim();

    if (classification !== QUICK_SHOP_CLASSIFICATION) return false;

    const today = new Date();

    // Block quick shop only while an early access campaign is active for the
    // shopper's market AND they don't already hold a grant for it (matches
    // the PDP's isEarlyAccessLocked behavior).
    if (this.isEarlyAccessGating(today)) return false;

    // Check for future launch date
    const launchDateStr = this.productData?.flags?.launchDate;
    if (launchDateStr && new Date(launchDateStr) > today) return false;

    return Boolean(this.getSizeOption()?.values.length);
  }

  /**
   * Mirrors the current-market date resolution used by
   * `snippets/product-early-access-state.liquid`'s `market_dates_json` output:
   * the product has an active Early Access campaign for the shopper's current
   * market if it has a resolvable campaign end date that is still in the
   * future. Deliberately does NOT check any "special type" tag — per the
   * Sprint 2 grooming transcript, this trigger is defined purely by the
   * linked campaign and its per-market date, not a separate classification
   * metafield.
   *
   * A shopper who already holds a grant for this product's campaign (same
   * `?earlyaccess=` check the PDP uses via `resolveEarlyAccessGrant`) is not
   * gated, mirroring `isEarlyAccessLocked` on the PDP buy stack.
   * @param {Date} today
   * @returns {boolean}
   */
  isEarlyAccessGating(today) {
    const marketDatesJson = this.productData?.flags?.earlyAccessMarketDatesJson;
    const marketKey = window.Shopify?.country;
    const expectedParam = this.productData?.flags?.earlyAccessUrlParam;
    const granted = Boolean(expectedParam) && resolveEarlyAccessGrant(expectedParam);

    return isEarlyAccessGatingQuickShop(marketDatesJson, marketKey, today, granted);
  }

  activate() {
    if (!this.$quickShop) return;

    this.$quickShop.hidden = false;
    this.renderSizeOptions();
    this.updateToggleState();
  }

  deactivate() {
    this.closeSizeSelector();
    this.$quickShop?.setAttribute('hidden', '');
    this.$grid?.replaceChildren();
  }

  getSizeOption() {
    const sizeKey = normalizeComparable(QUICK_SHOP_SIZE_OPTION_NAME);

    return (
      this.productData?.options?.find(
        (option) => normalizeComparable(option.name) === sizeKey
      ) || null
    );
  }

  renderSizeOptions() {
    const grid = this.$grid;
    if (!grid) return;

    const sizeOptions = this.buildSizeOptions();
    const gridColumns =
      sizeOptions.length < QUICK_SHOP_DEFAULT_COLUMNS
        ? 2
        : QUICK_SHOP_DEFAULT_COLUMNS;
    const emptyCellCount = this.getEmptyCellCount(
      sizeOptions.length,
      gridColumns
    );
    const fragment = document.createDocumentFragment();

    grid.setAttribute('data-size-count', String(sizeOptions.length));
    grid.setAttribute('data-grid-columns', String(gridColumns));

    sizeOptions.forEach((sizeOption) => {
      fragment.appendChild(this.createSizeCell(sizeOption));
    });

    for (let index = 0; index < emptyCellCount; index += 1) {
      const emptyCell = document.createElement('div');
      emptyCell.className =
        'product-card-quick-shop__cell product-card-quick-shop__cell--empty';
      emptyCell.setAttribute('aria-hidden', 'true');
      fragment.appendChild(emptyCell);
    }

    grid.replaceChildren(fragment);
  }

  buildSizeOptions() {
    const sizeOption = this.getSizeOption();
    if (!sizeOption) return [];

    return sizeOption.values.map((sizeValue) => {
      const matchingVariants = this.getVariantsForSize(sizeValue.name);
      const availableVariant = matchingVariants.find(isVariantAvailable);
      const variant = availableVariant || matchingVariants[0] || null;

      return {
        value: sizeValue.name,
        label: sizeValue.name,
        variant,
        isAvailable: Boolean(availableVariant),
      };
    });
  }

  /**
   * @param {string} sizeValue
   * @returns {ProductVariant[]}
   */
  getVariantsForSize(sizeValue) {
    const sizeOption = this.getSizeOption();
    if (!sizeOption) return [];

    const selectedOptions = this.getSelectedNonSizeOptions();

    return this.productData.variants.filter((variant) => {
      if (
        !optionValuesMatch(
          getVariantOptionValue(variant, sizeOption.name),
          sizeValue
        )
      ) {
        return false;
      }

      return selectedOptions.every((option) =>
        optionValuesMatch(
          getVariantOptionValue(variant, option.name),
          option.value
        )
      );
    });
  }

  /** @returns {SelectedOption[]} */
  getSelectedNonSizeOptions() {
    const sizeOption = this.getSizeOption();
    const sizeKey = normalizeComparable(sizeOption?.name);

    return (this.productData.selectedVariant?.selectedOptions || []).filter(
      (option) => normalizeComparable(option.name) !== sizeKey
    );
  }

  /**
   * @param {number} sizeCount
   * @param {number} gridColumns
   * @returns {number}
   */
  getEmptyCellCount(sizeCount, gridColumns) {
    if (sizeCount <= 0) return 0;
    if (sizeCount === 1) return 1;
    if (sizeCount < QUICK_SHOP_DEFAULT_COLUMNS) return 0;

    return (gridColumns - (sizeCount % gridColumns)) % gridColumns;
  }

  /**
   * @param {{
   *   value: string,
   *   label: string,
   *   variant: ProductVariant|null,
   *   isAvailable: boolean
   * }} sizeOption
   * @returns {HTMLButtonElement}
   */
  createSizeCell(sizeOption) {
    const button = document.createElement('button');
    const isUnavailable = !sizeOption.isAvailable || !sizeOption.variant;
    const outOfStockText =
      getThemeTranslation('product_card_out_of_stock') || 'Out of stock';

    button.type = 'button';
    button.className = 'product-card-quick-shop__cell body-md';
    button.textContent = sizeOption.label;
    button.setAttribute('data-size-value', sizeOption.value);
    button.setAttribute('data-variant-id', sizeOption.variant?.id || '');
    button.classList.toggle('is-unavailable', isUnavailable);
    button.disabled = isUnavailable;
    button.setAttribute(
      'aria-label',
      isUnavailable
        ? `${sizeOption.label} - ${outOfStockText}`
        : sizeOption.label
    );

    return button;
  }

  /** @param {MouseEvent} event */
  handleToggleClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.isAdding) return;

    if (this.isOpen) {
      this.closeSizeSelector();
    } else {
      this.openSizeSelector();
    }
  }

  /** @param {MouseEvent} event */
  handleSizeClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.isAdding) return;
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('.product-card-quick-shop__cell');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const variant = this.findVariantById(button.dataset.variantId || '');
    if (!isVariantAvailable(variant)) return;

    this.addVariantToCart(/** @type {ProductVariant} */ (variant));
  }

  /** @param {KeyboardEvent} event */
  handleKeydown(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.closeSizeSelector();
    }
  }

  /** @param {PointerEvent} event */
  handleDocumentPointerDown(event) {
    if (!this.isOpen || !(event.target instanceof Node)) return;
    if (this.root.contains(event.target)) return;

    this.closeSizeSelector();
  }

  openSizeSelector() {
    this.renderSizeOptions();
    this.isOpen = true;
    if (this.$panel) this.$panel.hidden = false;
    this.root.classList.add('product-card-custom--quick-shop-open');
    this.updateToggleState();
  }

  closeSizeSelector() {
    this.isOpen = false;
    if (this.$panel) this.$panel.hidden = true;
    this.root.classList.remove('product-card-custom--quick-shop-open');
    this.updateToggleState();
  }

  updateToggleState() {
    if (!this.$toggle) return;

    const title = this.productData?.title ? ` ${this.productData.title}` : '';
    this.$toggle.classList.toggle('is-open', this.isOpen);
    this.$toggle.setAttribute('aria-expanded', String(this.isOpen));
    this.$toggle.setAttribute(
      'aria-label',
      this.isOpen ? 'Close quick shop' : `Quick shop${title}`
    );
  }

  /**
   * @param {string} variantId
   * @returns {ProductVariant|null}
   */
  findVariantById(variantId) {
    const numericVariantId = extractNumericId(variantId) || variantId;

    return (
      this.productData.variants.find((variant) => {
        return (
          variant.id === variantId ||
          (extractNumericId(variant.id) || variant.id) === numericVariantId
        );
      }) || null
    );
  }

  /** @param {ProductVariant} variant */
  async addVariantToCart(variant) {
    this.isAdding = true;
    this.$quickShop?.setAttribute('aria-busy', 'true');

    try {
      const response = await this.postVariantToCart(variant);
      const cart = await this.fetchCart();
      const variantId = extractNumericId(variant.id) || variant.id;

      this.root.dispatchEvent(
        new CartAddEvent(cart || undefined, String(variantId), {
          source: 'product-card-quick-shop',
          itemCount: cart?.item_count || 0,
          productId: this.productData.id,
          variantId: String(variantId),
          sections: response.sections,
        })
      );

      this.closeSizeSelector();
    } catch (error) {
      console.error('[product-card-custom] Quick shop add failed', error);
    } finally {
      this.isAdding = false;
      this.$quickShop?.removeAttribute('aria-busy');
    }
  }

  /** @param {ProductVariant} variant */
  async postVariantToCart(variant) {
    const formData = new FormData();
    const variantId = extractNumericId(variant.id) || variant.id;
    const sectionIds = getCartSectionIds();

    formData.append('id', String(variantId));
    formData.append('quantity', '1');

    if (this.productData?.flags?.earlyAccessMarketDatesJson) {
      formData.append('properties[_early_access_dates]', this.productData.flags.earlyAccessMarketDatesJson);
    }

    const captureAtOrderValue =
      this.productData?.flags?.captureAtOrder?.trim() || '';
    // Quick shop adds the stock variant. Custom-strung rackets and custom bats get
    // Capture at Order from the PDP flow only.
    const isStockTennisRacket = Boolean(this.productData?.flags?.hasTennisStringing);
    const isStockBat = Boolean(this.productData?.flags?.hasCustomBats);
    if (captureAtOrderValue && !isStockTennisRacket && !isStockBat) {
      formData.append('properties[_Capture at Order]', captureAtOrderValue);
    }

    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });
    const response = await fetch(
      Theme?.routes?.cart_add_url || '/cart/add.js',
      {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'application/json',
        },
      }
    );
    const data = await response.json();

    if (!response.ok || data.status) {
      this.root.dispatchEvent(
        new CartErrorEvent(
          String(this.root.id || this.productData.id || ''),
          data.message || 'Unable to add item to cart',
          data.description,
          data.errors
        )
      );
      throw new Error(data.message || `Cart add failed: HTTP ${response.status}`);
    }

    return data;
  }

  async fetchCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return null;

      return await response.json();
    } catch {
      return null;
    }
  }

  destroy() {
    this.abortController?.abort();
    this.abortController = null;
    this.deactivate();
    this.$quickShop?.removeAttribute('aria-busy');
    this.$toggle = null;
    this.$panel = null;
    this.$grid = null;
    this.$quickShop = null;
  }
}
